const KLAVIYO_REVISION = '2026-01-15';
const IDENTIFY_RETRY_MS = 250;
const IDENTIFY_MAX_ATTEMPTS = 20;

function getKlaviyoCompanyIdFromEmbed() {
  for (const script of document.querySelectorAll('script[src*="klaviyo"]')) {
    const src = script.getAttribute('src') || '';
    const match = src.match(/static\.klaviyo\.com\/onsite\/js\/([^/]+)\//);
    if (match) return match[1];
  }
  return null;
}

function getKlaviyoExchangeId() {
  const match = document.cookie.match(/(?:^|;\s*)__kla_id=([^;]*)/);
  if (!match) return null;

  try {
    const raw = decodeURIComponent(match[1]).replace(/^"|"$/g, '');
    const decoded = atob(raw);
    const parsed = JSON.parse(decoded);
    return parsed.$exchange_id || null;
  } catch {
    return null;
  }
}

/**
 * Tells Klaviyo.js who the visitor is so __kla_id cookie and future events
 * are linked to the logged-in Shopify customer profile.
 */
function identifyWithKlaviyo(customer, attempt = 0) {
  if (!customer?.email) return;

  const props = { email: customer.email };
  if (customer.firstName) props.first_name = customer.firstName;
  if (customer.lastName) props.last_name = customer.lastName;

  if (typeof window.klaviyo?.identify === 'function') {
    window.klaviyo.identify(props);
    return;
  }

  if (Array.isArray(window._learnq)) {
    const legacyProps = { $email: customer.email };
    if (customer.firstName) legacyProps.$first_name = customer.firstName;
    if (customer.lastName) legacyProps.$last_name = customer.lastName;
    window._learnq.push(['identify', legacyProps]);
    return;
  }

  if (attempt < IDENTIFY_MAX_ATTEMPTS) {
    setTimeout(() => identifyWithKlaviyo(customer, attempt + 1), IDENTIFY_RETRY_MS);
  }
}

/**
 * Builds the profile attributes for a Client Events payload.
 *
 * IMPORTANT: only a *real* identifier is ever attached — the visitor's email
 * (logged-in Shopify customer) or the `_kx` exchange id from the existing
 * Klaviyo cookie. We deliberately never fabricate an `anonymous_id`: sending a
 * new one makes Klaviyo mint a brand-new, empty, email-less profile on every
 * anonymous add-to-cart. Returns an empty object when the visitor is not yet
 * identified, and the caller must skip the event in that case.
 */
function buildProfileAttributes(customer, exchangeId) {
  const attrs = {};

  if (customer?.email) {
    attrs.email = customer.email;
    if (customer.firstName) attrs.first_name = customer.firstName;
    if (customer.lastName) attrs.last_name = customer.lastName;
  }

  if (exchangeId) {
    attrs._kx = exchangeId;
  }

  return attrs;
}

function hasIdentifier(profileAttributes) {
  return Boolean(profileAttributes.email || profileAttributes._kx);
}

function normalizeCartItems(body) {
  if (!body) return [];
  if (Array.isArray(body.items)) return body.items.filter(Boolean);
  if (body.product_title != null || body.id != null) return [body];
  return [];
}

function toAbsoluteUrl(url) {
  if (!url) return '';
  if (url.startsWith('//')) return `https:${url}`;
  if (url.startsWith('http')) return url;
  return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
}

function findVariantInPageJson(variantId) {
  const selectors = ['#lp-variant-data', 'script[data-lp-card-variants]'];

  for (const selector of selectors) {
    for (const el of document.querySelectorAll(selector)) {
      try {
        const variants = JSON.parse(el.textContent || '[]');
        if (!Array.isArray(variants)) continue;
        const match = variants.find((v) => String(v.id) === String(variantId));
        if (match) return match;
      } catch {
        continue;
      }
    }
  }

  return null;
}

function buildProductUrl(item) {
  const handle = item.handle;
  const variantId = item.id ?? item.variant_id;
  const onProductPage = handle && window.location.pathname.includes(`/products/${handle}`);

  let baseUrl;
  if (onProductPage) {
    baseUrl = window.location.href.split('#')[0];
  } else if (item.url) {
    baseUrl = toAbsoluteUrl(item.url);
  } else if (handle) {
    const root = window.Shopify?.routes?.root || '/';
    baseUrl = `${window.location.origin}${root}products/${handle}?variant=${variantId}`;
  } else {
    baseUrl = window.location.href.split('#')[0];
  }

  const url = new URL(baseUrl, window.location.origin);
  url.searchParams.set('cart', 'true');
  return url.toString();
}

function resolveCompareAtPrice(item, unitPrice) {
  if (item.compare_at_price != null && item.compare_at_price > 0) {
    return item.compare_at_price / 100;
  }

  const variantId = item.id ?? item.variant_id;
  const pageVariant = variantId ? findVariantInPageJson(variantId) : null;
  if (pageVariant?.compare_at_price != null && pageVariant.compare_at_price > 0) {
    return pageVariant.compare_at_price / 100;
  }

  return unitPrice;
}

function buildEventProperties(item) {
  const unitPrice = (item.price ?? 0) / 100;
  const compareAtPrice = resolveCompareAtPrice(item, unitPrice);
  const imageUrl = toAbsoluteUrl(
    item.featured_image?.url || item.image || item.image_url || '',
  );

  return {
    Name: item.product_title || item.title || '',
    ProductID: item.product_id,
    Categories: item.product_type || '',
    ImageURL: imageUrl,
    URL: buildProductUrl(item),
    Brand: item.vendor || '',
    Price: unitPrice,
    CompareAtPrice: compareAtPrice,
  };
}

function buildEventPayload(item, profileAttributes) {
  const properties = buildEventProperties(item);
  const variantId = item.id ?? item.variant_id ?? 'unknown';

  return {
    data: {
      type: 'event',
      attributes: {
        properties,
        metric: {
          data: {
            type: 'metric',
            attributes: { name: 'Added to Cart' },
          },
        },
        profile: {
          data: {
            type: 'profile',
            attributes: profileAttributes,
          },
        },
        value: 0,
        unique_id: `atc-${variantId}-${Date.now()}`,
      },
    },
  };
}

if (!customElements.get('klaviyo-added-to-cart')) {
  customElements.define(
    'klaviyo-added-to-cart',
    class KlaviyoAddedToCart extends HTMLElement {
      constructor() {
        super();
        this.onCartRequestEnd = this.onCartRequestEnd.bind(this);
      }

      get customerContext() {
        const email = (this.dataset.customerEmail || '').trim();
        if (!email) return null;

        return {
          email,
          firstName: (this.dataset.customerFirstName || '').trim(),
          lastName: (this.dataset.customerLastName || '').trim(),
        };
      }

      connectedCallback() {
        document.addEventListener('liquid-ajax-cart:request-end', this.onCartRequestEnd);

        const customer = this.customerContext;
        if (customer) {
          identifyWithKlaviyo(customer);
        }
      }

      disconnectedCallback() {
        document.removeEventListener('liquid-ajax-cart:request-end', this.onCartRequestEnd);
      }

      onCartRequestEnd(event) {
        const { requestState } = event.detail || {};

        if (requestState?.requestType !== 'add' || !requestState?.responseData?.ok) {
          return;
        }

        const companyId = getKlaviyoCompanyIdFromEmbed();
        if (!companyId) return;

        const items = normalizeCartItems(requestState.responseData.body);
        if (!items.length) return;

        const customer = this.customerContext;
        const exchangeId = getKlaviyoExchangeId();
        const profileAttributes = buildProfileAttributes(customer, exchangeId);

        // Don't log the metric for unidentified visitors. Firing without a real
        // identifier (email or existing Klaviyo `_kx`) would create an empty,
        // unusable profile in Klaviyo on every anonymous add-to-cart.
        if (!hasIdentifier(profileAttributes)) return;

        for (const item of items) {
          this.trackItem(item, companyId, profileAttributes);
        }
      }

      trackItem(item, companyId, profileAttributes) {
        const payload = buildEventPayload(item, profileAttributes);
        const url = `https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(companyId)}`;

        const headers = new Headers();
        headers.set('Content-Type', 'application/vnd.api+json');
        headers.set('revision', KLAVIYO_REVISION);

        fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) }).catch(() => {});
      }
    },
  );
}
